import { useEffect } from 'react';
import { listenForDesktopReturnLinks, type StopListeningType } from './desktopReturnLink';
import { desktopReturnState } from './desktopReturnStamp';

/** The part of a data router this needs, so a test can hand it a stub (`readerNavigation.ts`'s `INavigationSource` is the same idea, for the same reason). */
export interface INavigationTarget {
  navigate(to: string, options: { state: unknown }): unknown;
}

/**
 * Routes a `featit-return` link to the path and query it carries, in the window the person started
 * from — the same client-side navigation an in-app link already performs, landing on the same
 * `LoginPage`/`ProfilePage`/billing-settings routes a *web* return already reads (I-1078).
 *
 * Takes the router directly rather than `useNavigate()`: a link can arrive before this component's
 * own route has mounted, or from a Tauri event outside any render, and the module-level router
 * singleton is the one piece every caller — this hook, `subscribeToReaderNavigation` — already
 * shares for exactly that reason.
 *
 * Each navigation carries a fresh stamp in its state (`desktopReturnStamp.ts`, AC-29): the page is
 * never replaced on the desktop, so a screen still mounted reads its data again on the stamp.
 */
export function useDesktopReturnLinks(router: INavigationTarget): void {
  useEffect(() => {
    let stop: StopListeningType = () => {};
    let disposed = false;
    void listenForDesktopReturnLinks((path) => {
      void router.navigate(path, { state: desktopReturnState() });
    }).then((unlisten) => {
      if (disposed) {
        unlisten();
      } else {
        stop = unlisten;
      }
    });
    return () => {
      disposed = true;
      stop();
    };
  }, [router]);
}
